import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageSquare } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { ConversationList } from "@/components/messages/ConversationList";
import { Thread } from "@/components/messages/Thread";
import { ConvPlanBar, PlanButton, PlanStatus } from "@/components/admin/ConvPlanBar";
import { CATEGORIES, READ_EVENT, UnreadBadge } from "@/components/messages/unread";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const SELECT = "min-w-0 rounded-md border border-neutral-700 bg-black/50 px-2 py-1.5 text-xs text-white";

export default function AdminMessages() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ status: "", category: "", unread: false });
  const [list, setList] = useState(null);
  const [conv, setConv] = useState(null);
  const [dev, setDev] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [planUnread, setPlanUnread] = useState(0);
  const activeId = params.get("c");
  const developer = params.get("developer");
  const load = useCallback(() => {
    const q = { ...(filters.status && { status: filters.status }), ...(filters.category && (["dev_plan", "minisite_plan", "question"].includes(filters.category) ? { thread: filters.category } : { category: filters.category })), ...(filters.unread && { unread: true }), ...(developer && { developer_id: developer }) };
    api.get("/admin/messages/unread").then(({ data }) => setPlanUnread(data.plan_requests || 0)).catch(() => {});
    return api.get("/admin/messages", { params: q }).then(({ data }) => setList(data));
  }, [filters, developer]);
  useEffect(() => {
    load();
  }, [load]);
  const loadConv = useCallback(() => {
    if (!activeId) return setConv(null);
    return api.get(`/admin/messages/${activeId}`).then(({ data }) => {
      setConv(data);
      window.dispatchEvent(new Event(READ_EVENT));
    }).catch(() => setConv(null));
  }, [activeId]);
  useEffect(() => {
    loadConv();
    setPlanOpen(false);
  }, [loadConv]);
  const devId = conv?.developer.id;
  const loadDev = useCallback(() => {
    if (devId) api.get(`/admin/developers/${devId}`).then(({ data }) => setDev(data)).catch(() => setDev(null));
  }, [devId]);
  useEffect(() => {
    setDev(null);
    loadDev();
  }, [loadDev]);
  const openPlan = () => {
    setPlanOpen(true);
    document.getElementById("conv-plan-bar")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const planAction = (m) => (m.action.type === "plan_request" ? (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <PlanButton d={dev} onClick={openPlan} testId={`msg-plan-create-${m.id}`} />
      <PlanStatus d={dev} testId={`msg-plan-status-${m.id}`} />
    </div>
  ) : null);
  const toggleTreated = async () => {
    const { data } = await api.put(`/admin/messages/${activeId}/treated`, { treated: !conv.treated });
    setConv(data);
    load();
  };
  const removeThread = async () => {
    if (!window.confirm(t("msg.deleteConfirm"))) return;
    try {
      await api.delete(`/admin/messages/${activeId}`);
      toast.success(t("msg.deleted"));
      setParams(developer ? { developer } : {});
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const TABS = [["", "msg.tabAll"], ["dev_plan", "msg.type.dev_plan"], ["minisite_plan", "msg.type.minisite_plan"], ["question", "msg.type.question"]];
  const update = async (fn) => {
    try {
      const { data } = await fn();
      setConv(data);
      if (data.notified === false) toast.warning(t("msg.notNotified"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div data-testid="admin-messages-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("msg.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("msg.adminIntro")}</p>
      </PageHero>
      <div className="mx-auto grid max-w-6xl gap-4 px-4 pb-16 sm:px-8 lg:grid-cols-[360px_1fr]">
        <section className={`card-carbon min-w-0 overflow-hidden rounded-2xl ${conv ? "hidden lg:block" : ""}`}>
          <div className="space-y-3 border-b border-neutral-800 p-4">
            <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><MessageSquare className="h-4 w-4 text-[var(--gold)]" /> {t("msg.title")}</h2>
            <div className="flex flex-wrap gap-1.5" role="tablist" data-testid="msg-tabs">
              {TABS.map(([v, label]) => (
                <button key={v || "all"} type="button" role="tab" aria-selected={(filters.category || "") === v}
                  onClick={() => setFilters((f) => ({ ...f, category: v }))} data-testid={`msg-tab-${v || "all"}`}
                  className={`inline-flex max-w-full items-center rounded-full border px-3 py-1 text-xs font-bold transition-colors duration-150 ${(filters.category || "") === v ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-neutral-700 text-neutral-200 hover:border-[var(--gold)]"}`}>
                  {t(label)}{v === "dev_plan" && <UnreadBadge count={planUnread} testId="msg-tab-plan-unread" />}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={filters.status} onChange={setF("status")} aria-label={t("msg.statusAll")} className={SELECT} data-testid="msg-filter-status">
                <option value="">{t("msg.statusAll")}</option>
                <option value="open">{t("msg.open")}</option>
                <option value="closed">{t("msg.closed")}</option>
              </select>
              <select value={filters.category} onChange={setF("category")} aria-label={t("msg.catAll")} className={SELECT} data-testid="msg-filter-category">
                <option value="">{t("msg.catAll")}</option>
                <option value="questions">{t("msg.tabQuestions")}</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`msg.cat.${c}`)}</option>)}
              </select>
              <label className="inline-flex items-center gap-1.5 text-xs text-neutral-300">
                <input type="checkbox" checked={filters.unread} onChange={setF("unread")} data-testid="msg-filter-unread" /> {t("msg.unreadOnly")}
              </label>
            </div>
          </div>
          {list === null ? <Loader2 className="m-4 h-5 w-5 animate-spin text-[var(--gold)]" /> : <ConversationList items={list} activeId={activeId} onOpen={(id) => setParams({ c: id, ...(developer && { developer }) })} admin />}
        </section>
        <section className={`card-carbon min-w-0 overflow-hidden rounded-2xl ${conv ? "" : "hidden lg:block"}`}>
          {conv && <ConvPlanBar conv={conv} d={dev} open={planOpen} setOpen={setPlanOpen} onChanged={() => { loadConv(); loadDev(); load(); }} onTreated={toggleTreated} onDelete={removeThread} />}
          {conv ? (
            <Thread conv={conv} admin renderAction={planAction} onBack={() => setParams(developer ? { developer } : {})}
              onSend={(body) => update(() => api.post(`/admin/messages/${conv.id}`, { body }))}
              onStatus={(status) => update(() => api.put(`/admin/messages/${conv.id}/status`, { status }))} />
          ) : (
            <p className="p-6 text-sm text-neutral-400" data-testid="msg-select-hint">{t("msg.select")}</p>
          )}
        </section>
      </div>
    </div>
  );
}
